/**
 * #2392 (owner 2026-10-28): the one rule for a table the source sent no rows for in an import,
 * and its words, read by the run page, the connection's Imports tab and the run message.
 *
 * - The source sent no row and no column, and the table did not exist: the load did not create it
 *   (no placeholder table). It reads "No rows yet, table not created".
 * - The table exists and this import brought no row (an empty table made with its real columns,
 *   or one an earlier import made, left as it is): it reads "No rows", never the green Loaded.
 *
 * Neither is a failure, and neither changes the run's status.
 */

/** The neutral status of a table this import brought no row for. */
export const NO_ROWS_LABEL = "No rows";

/** The status of a table the source sent nothing for and the load never created. */
export const NOT_CREATED_LABEL = "No rows yet, table not created";

/** The hover over either status. */
export const NO_ROWS_HOVER = "The source returned no rows for this table in this import.";

/** The hover over the not created status: why, and when the table appears. */
export const NOT_CREATED_HOVER =
  "The source returned no rows for this table in this import. The table is created in your warehouse at the first import that brings its rows or columns.";

export type TableNoRows = "no_rows" | "not_created";

/** What `tableNoRows` reads of a table's row: its extracted count and its load outcome. */
export interface NoRowsInput {
  records: number;
  load?: {
    status: string;
    rowsLoaded: number | null;
    noRows?: "not_created" | "kept";
  } | null;
}

/**
 * Whether this import brought the table no row: `not_created` when the load did not create it,
 * `no_rows` when the table exists and nothing arrived (a load that succeeded with 0 rows loaded
 * and 0 extracted, or a table the load left as it was), else null. A load not yet counted (the
 * live outcome, `rowsLoaded` null) is never "No rows".
 */
export function tableNoRows(row: NoRowsInput): TableNoRows | null {
  const load = row.load;
  if (!load) return null;
  if (load.noRows === "not_created") return "not_created";
  if (load.noRows === "kept") return "no_rows";
  if (load.status === "succeeded" && load.rowsLoaded === 0 && row.records === 0) return "no_rows";
  return null;
}

function tables(count: number): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? "table" : "tables"}`;
}

/**
 * The count of a load's tables with the empty ones apart: "12 tables loaded, 8 had no rows" when
 * every table given to the load is one or the other, "12 of 30 tables loaded, 8 had no rows" when
 * some did neither, and "12 of 30 tables loaded" when none was empty. `loaded` counts only the
 * tables that brought rows.
 */
export function tablesLoadedSentence(input: {
  loaded: number;
  noRows: number;
  given: number;
}): string {
  const { loaded, noRows, given } = input;
  const of = `${loaded.toLocaleString("en-US")} of ${tables(given)} loaded`;
  if (noRows === 0) return of;
  const had = `${noRows.toLocaleString("en-US")} had no rows`;
  if (loaded + noRows === given) return `${tables(loaded)} loaded, ${had}`;
  return `${of}, ${had}`;
}

/**
 * #2392 (review): an import whose every table the source sent nothing for: "The source returned no
 * rows for any of the 20 tables", never "0 rows into 0 tables". No full stop, the caller adds it.
 */
export function allTablesEmptySentence(count: number): string {
  return count === 1
    ? "The source returned no rows for the table"
    : `The source returned no rows for any of the ${count.toLocaleString("en-US")} tables`;
}

/** #2392 (review): the Events line of a table the source sent nothing for, by its outcome. */
export function noRowsEventSentence(noRows: unknown): string | null {
  if (noRows === "not_created") return "no rows yet, table not created.";
  if (noRows === "kept") return "no rows in this import.";
  return null;
}
